import {
    type PurchasedItinerariesState,
    type PurchasedItineraryEntry,
    type PurchasedSegmentData,
    type StoredPurchasedTicket,
} from "../store/persistence/slices/purchasedItineraries.slice";
import { type SavedItineraryEntry } from "../store/persistence/slices/savedItineraries.slice";
import { type ItineraryDTO } from "../types/dtos/api/itinerary/itinerary.dto";
import { type CreditNoteSummaryDTO } from "../types/dtos/api/purchase/credit-note-summary.dto";
import { type PurchasedBookingDTO } from "../types/dtos/api/purchase/purchase-bookings.dto";
import { type PaymentMethodSummaryDTO } from "../types/dtos/api/purchase/purchase-payment.dto";
import { type OfferSegmentSalesDTO } from "../types/dtos/api/sales/offer.dto";

/** Maximum number of purchased itineraries kept locally (oldest entries are evicted). */
export const PURCHASED_ITINERARIES_MAX_ITEMS = 50;

/** One bought segment: the prebooked offer identity plus its charged price. */
export interface PurchasedSegmentInput {
    "preBookingId":    string;
    "segmentId":       string;
    "offerIdentifier": string;
    "productId"?:      string | null;
    "priceCents"?:     number;
    "validityPeriod"?: {
        "from": string;
        "to":   string;
    };
}

/** Parameters of {@link buildPurchasedItineraryEntry}. */
export interface BuildPurchasedItineraryEntryParams {
    "purchaseAttemptId":   string;
    "provider":            string;
    "providerBookingId":   string | null;
    "conversationId":      string;
    "purchasedAt":         number;
    "segments":            readonly PurchasedSegmentInput[];
    "tickets":             readonly StoredPurchasedTicket[];
    "ticketFilesComplete": boolean;
    "totalPriceCents"?:    number;
    "currency"?:           string;

    /** Display-safe payment method summary — `null`/absent when unknown. */
    "paymentMethod"?: PaymentMethodSummaryDTO | null;

    /** FlowRide booking row id — invoice download key. */
    "bookingId"?: string;

    /** Invoice number (`FW-YYYY-NNNNNN`) — `null` while issuance is pending. */
    "invoiceNumber"?: string | null;

    /** Credit notes issued against the booking's invoice — empty at booking time. */
    "creditNotes"?:      CreditNoteSummaryDTO[];
    "originLabel"?:      string;
    "destinationLabel"?: string;
}

/** Offer segment carrying injected ticket data (`offer.segments[id].purchased`). */
type OfferSegmentWithPurchase = OfferSegmentSalesDTO & { "purchased"?: PurchasedSegmentData };

/**
 * Clones an itinerary snapshot so injected purchase data cannot leak into live planner state.
 *
 * @param itinerary Itinerary to copy.
 * @returns Deep copy safe to enrich and persist.
 */
const cloneItineraryForPurchase = ( itinerary: ItineraryDTO ): ItineraryDTO => {
    if ( typeof structuredClone === "function" ) {
        return structuredClone( itinerary );
    }

    return JSON.parse( JSON.stringify( itinerary ) ) as ItineraryDTO;
};

/**
 * Returns whether a saved-list entry is a purchased itinerary.
 *
 * @param entry Saved or purchased list entry.
 * @returns `true` for purchased entries (discriminated on `type`).
 */
export const isPurchasedItineraryEntry = ( entry: SavedItineraryEntry | PurchasedItineraryEntry ): entry is PurchasedItineraryEntry => {
    return "type" in entry && entry.type === "purchased";
};

/**
 * Reads the injected purchase data of one offer segment.
 *
 * @param itinerary Itinerary possibly carrying purchased segment data.
 * @param segmentId `offer.segments` key (`pricingSegment|{providerId}|{firstLegIndex}|{lastLegIndex}`).
 * @returns Injected segment purchase data, or `undefined` when absent.
 */
export const getPurchasedSegmentData = (
    itinerary: ItineraryDTO,
    segmentId: string,
): PurchasedSegmentData | undefined => {
    const segment = itinerary.offer?.segments?.[segmentId] as OfferSegmentWithPurchase | undefined;

    return segment?.purchased;
};

/**
 * Returns whether an itinerary carries at least one purchased-segment marker.
 *
 * @param itinerary Itinerary possibly carrying purchased segment data.
 * @returns `true` when any `offer.segments` entry has `purchased` injected.
 */
export const hasPurchasedSegments = ( itinerary: ItineraryDTO ): boolean => {
    return Object.keys( itinerary.offer?.segments ?? {} ).some( ( segmentId ) => getPurchasedSegmentData( itinerary, segmentId ) !== undefined );
};

/**
 * Collects every stored ticket of a purchased itinerary (segments plus unmatched).
 *
 * @param entry Purchased itinerary entry.
 * @returns Flat ticket list.
 */
export const getPurchasedItineraryTickets = ( entry: PurchasedItineraryEntry ): StoredPurchasedTicket[] => {
    const segmentTickets = Object.keys( entry.itinerary.offer?.segments ?? {} ).
        flatMap( ( segmentId ) => getPurchasedSegmentData( entry.itinerary, segmentId )?.tickets ?? [] );

    return [ ...segmentTickets, ...entry.purchase.unmatchedTickets ];
};

/**
 * Builds a purchased itinerary entry: clones the itinerary and injects each bought
 * segment's ticket data into `offer.segments[segmentId].purchased`.
 *
 * @param itinerary Itinerary snapshot the purchase was made from.
 * @param params Purchase outcome (attempt, booking, segments, tickets).
 * @returns Entry ready to upsert into the purchased slice.
 */
export const buildPurchasedItineraryEntry = (
    itinerary: ItineraryDTO,
    params: BuildPurchasedItineraryEntryParams,
): PurchasedItineraryEntry => {
    const storedItinerary = cloneItineraryForPurchase( itinerary );
    const attachedPreBookingIds = new Set<string>();

    for ( const segment of params.segments ) {
        const offerSegment = storedItinerary.offer?.segments?.[segment.segmentId] as OfferSegmentWithPurchase | undefined;
        if ( offerSegment === undefined ) {
            continue;
        }

        attachedPreBookingIds.add( segment.preBookingId );
        offerSegment.purchased = {
            "preBookingId":    segment.preBookingId,
            "offerIdentifier": segment.offerIdentifier,
            "productId":       segment.productId,
            "priceCents":      segment.priceCents,
            "validityPeriod":  segment.validityPeriod,
            "tickets":         params.tickets.filter( ( ticket ) => ticket.preBookingId === segment.preBookingId ),
        };
    }

    const unmatchedTickets = params.tickets.filter( ( ticket ) => !attachedPreBookingIds.has( ticket.preBookingId ) );

    return {
        "itinerary": storedItinerary,
        "type":      "purchased",
        "savedAt":   params.purchasedAt,
        "purchase":  {
            "purchaseAttemptId":   params.purchaseAttemptId,
            "provider":            params.provider,
            "providerBookingId":   params.providerBookingId,
            "conversationId":      params.conversationId,
            "purchasedAt":         params.purchasedAt,
            "totalPriceCents":     params.totalPriceCents,
            "currency":            params.currency,
            "ticketFilesComplete": params.ticketFilesComplete,
            "paymentMethod":       params.paymentMethod ?? null,
            "bookingId":           params.bookingId,
            "invoiceNumber":       params.invoiceNumber ?? null,
            "creditNotes":         params.creditNotes ?? [],
            unmatchedTickets,
        },
        ...params.originLabel !== undefined ? { "originLabel": params.originLabel } : {},
        ...params.destinationLabel !== undefined ? { "destinationLabel": params.destinationLabel } : {},
    };
};

/**
 * Inserts or replaces a purchased itinerary entry.
 *
 * @param entries Current purchased entries.
 * @param entry Entry to store (replaced in place when `purchaseAttemptId` already exists).
 * @returns Updated list capped at {@link PURCHASED_ITINERARIES_MAX_ITEMS}.
 */
export const upsertPurchasedItinerary = (
    entries: PurchasedItinerariesState,
    entry: PurchasedItineraryEntry,
): PurchasedItinerariesState => {
    const existingIndex = entries.findIndex( ( item ) => item.purchase.purchaseAttemptId === entry.purchase.purchaseAttemptId );

    const nextEntries = existingIndex >= 0
        ? entries.map( ( item, index ) => ( index === existingIndex ? entry : item ) )
        : [ entry, ...entries ];

    return nextEntries.slice( 0, PURCHASED_ITINERARIES_MAX_ITEMS );
};

/**
 * Returns whether every ticket of a purchased segment has been refunded.
 *
 * @param purchased Purchased segment data (`offer.segments[segmentId].purchased`).
 * @returns `true` when the segment has at least one ticket and all are refunded or pending payment credit.
 */
export const isPurchasedSegmentFullyRefunded = ( purchased: PurchasedSegmentData | undefined ): boolean => {
    if ( purchased === undefined || purchased.tickets.length === 0 ) {
        return false;
    }

    return purchased.tickets.every( ( ticket ) => ticket.refundStatus === "refunded" || ticket.refundStatus === "refund_pending" );
};

/**
 * Updates the tickets of a purchased itinerary entry in place after a user
 * refund confirmation, matching on `purchase.bookingId`.
 *
 * @param entries Current persisted purchased itinerary entries.
 * @param bookingId FlowRide booking row id (`purchase.bookingId`).
 * @param updatedTickets Updated ticket list returned by `POST /api/purchase/bookings/:id/refund`.
 * @returns Updated purchased itinerary entries.
 */
export const applyBookingRefundToEntries = (
    entries: PurchasedItinerariesState,
    bookingId: string,
    updatedTickets: readonly StoredPurchasedTicket[],
): PurchasedItinerariesState => {
    const byProviderTicketId = new Map<string, StoredPurchasedTicket>();

    for ( const ticket of updatedTickets ) {
        if ( !byProviderTicketId.has( ticket.providerTicketId ) ) {
            byProviderTicketId.set( ticket.providerTicketId, ticket );
        }
    }

    const mergeTicket = ( existing: StoredPurchasedTicket ): StoredPurchasedTicket => {
        const updated = byProviderTicketId.get( existing.providerTicketId );

        if ( updated === undefined ) {
            return existing;
        }

        return {
            ...existing,
            "refundStatus":           updated.refundStatus,
            "refundedAmountCents":    updated.refundedAmountCents,
            "refundRetainedFeeCents": updated.refundRetainedFeeCents,
            "refundedAt":             updated.refundedAt,
        };
    };

    return entries.map( ( entry ) => {
        if ( entry.purchase.bookingId !== bookingId ) {
            return entry;
        }

        const clonedItinerary = cloneItineraryForPurchase( entry.itinerary );

        for ( const segmentId of Object.keys( clonedItinerary.offer?.segments ?? {} ) ) {
            const segment = clonedItinerary.offer?.segments?.[segmentId] as OfferSegmentWithPurchase | undefined;

            if ( segment?.purchased !== undefined ) {
                segment.purchased = {
                    ...segment.purchased,
                    "tickets": segment.purchased.tickets.map( mergeTicket ),
                };
            }
        }

        return {
            ...entry,
            "itinerary": clonedItinerary,
            "purchase":  {
                ...entry.purchase,
                "unmatchedTickets": entry.purchase.unmatchedTickets.map( mergeTicket ),
            },
        };
    } );
};

/**
 * Refreshes the credit note list of a purchased itinerary entry in place,
 * matching on `purchase.bookingId`.
 *
 * Called after a refund confirmation — the credit note may still be issuing
 * server-side, so a later `/bookings` sync remains the fallback.
 *
 * @param entries Current persisted purchased itinerary entries.
 * @param bookingId FlowRide booking row id (`purchase.bookingId`).
 * @param creditNotes Credit note list returned by `GET /api/purchase/bookings/:id/credit-notes`.
 * @returns Updated purchased itinerary entries.
 */
export const applyCreditNotesToEntries = (
    entries: PurchasedItinerariesState,
    bookingId: string,
    creditNotes: readonly CreditNoteSummaryDTO[],
): PurchasedItinerariesState => {
    return entries.map( ( entry ) => ( entry.purchase.bookingId !== bookingId
        ? entry
        : {
            ...entry,
            "purchase": {
                ...entry.purchase,
                "creditNotes": [ ...creditNotes ],
            },
        } ) );
};

/**
 * Maps the server booking list to persisted purchased entries.
 *
 * @param bookings `GET /api/purchase/bookings` response (newest first).
 * @returns Purchased entries preserving the server order — the cap keeps the newest.
 */
export const buildPurchasedEntriesFromServer = ( bookings: readonly PurchasedBookingDTO[] ): PurchasedItinerariesState => {
    const seenAttemptIds = new Set<string>();
    const entries: PurchasedItinerariesState = [];

    for ( const booking of bookings ) {
        if ( seenAttemptIds.has( booking.purchaseAttemptId ) ) {
            continue;
        }
        seenAttemptIds.add( booking.purchaseAttemptId );

        const purchasedAt = Date.parse( booking.bookedAt );
        entries.push( buildPurchasedItineraryEntry( booking.itinerary, {
            "purchaseAttemptId": booking.purchaseAttemptId,
            "provider":          booking.provider,
            "providerBookingId": booking.providerBookingId,
            "conversationId":    booking.conversationId,
            "purchasedAt":       Number.isFinite( purchasedAt ) ? purchasedAt : Date.now(),
            "segments":          booking.segments.map( ( segment ) => ( {
                "preBookingId":    segment.preBookingId,
                "segmentId":       segment.segmentId,
                "offerIdentifier": segment.offerIdentifier,
                "productId":       segment.productId,
                "priceCents":      segment.priceCents,
                "validityPeriod":  segment.validityPeriod,
            } ) ),
            "tickets":             booking.tickets,
            "ticketFilesComplete": booking.ticketFilesComplete,
            "totalPriceCents":     Math.round( booking.totalPrice * 100 ),
            "currency":            booking.currency,
            "paymentMethod":       booking.paymentMethod,
            "bookingId":           booking.bookingId,
            "invoiceNumber":       booking.invoiceNumber,
            "creditNotes":         booking.creditNotes,
        } ) );
    }

    return entries.slice( 0, PURCHASED_ITINERARIES_MAX_ITEMS );
};
